import { useRouter } from "expo-router";
import { useState } from "react";
import { StyleSheet, Text, View } from "react-native";

import { useAuth } from "../auth";
import { trpc } from "../trpc";
import { colors, fonts } from "../theme";
import { Button, Card } from "./brand";

// Account hook-in for a finished result, shared by all three calculators.
// `calc.claimAssessment` is product-agnostic (it claims by id + ownership), so
// the same card saves an AU-08, AU-06 or AU-13 run. Anonymous visitors route to
// /auth carrying the result id (claim=…) so signing in attaches it; signed-in
// visitors claim inline (idempotent if it already landed under them at calc
// time). Information-only copy: this is account chrome, no advice framing
// (validation §3).
export function SaveCard({ id }: { id: string }): JSX.Element {
  const router = useRouter();
  const { status } = useAuth();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === "loading") return <View />;

  if (status === "anon") {
    return (
      <Card tone="sand">
        <Text style={styles.title}>Keep this result</Text>
        <Text style={styles.muted}>
          Create a free account to save your calculations. We store only your
          email — never your income or bank details.
        </Text>
        <Button
          label="Sign in to save"
          testID="save-signin"
          onPress={() =>
            router.push({
              pathname: "/auth",
              params: { claim: id, next: "/my-results" },
            })
          }
        />
      </Card>
    );
  }

  if (saved) {
    return (
      <Card tone="sand">
        <Text style={styles.title}>Saved to your account</Text>
        <Button
          label="View my results"
          testID="save-view"
          onPress={() => router.push("/my-results")}
        />
      </Card>
    );
  }

  const onSave = (): void => {
    if (saving) return;
    setSaving(true);
    setError(null);
    trpc.calc.claimAssessment
      .mutate({ id })
      .then(() => setSaved(true))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Couldn't save.");
      })
      .finally(() => setSaving(false));
  };

  return (
    <Card tone="sand">
      <Text style={styles.title}>Save this result</Text>
      <Button
        label={saving ? "Saving…" : "Save to my account"}
        testID="save-now"
        onPress={onSave}
      />
      {error !== null ? <Text style={styles.muted}>{error}</Text> : null}
    </Card>
  );
}

const styles = StyleSheet.create({
  title: { fontFamily: fonts.display, fontSize: 18, color: colors.ink },
  muted: { fontFamily: fonts.body, fontSize: 14, lineHeight: 20, color: colors.inkSoft },
});
